import React from "react";
import { Link } from "react-router-dom";
import bdePackages from "../../../JsonFiles/RegisterNewCoursePackages.json";
import inVehicleOptions from "../../../JsonFiles/NewCourseInvehicleTrainingOptions.json";
import driveTestOptions from "../../../JsonFiles/NewCourseDriveTestOptions.json";

const LICENSE_OPTIONS = ["G1", "G2", "G"];

/** Number badge first, then a separate checkbox that reveals options. */
function SectionToggle({ number, title, checked, onChange }) {
  return (
    <div className="flex w-fit select-none items-center gap-3">
      <span className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-full bg-[#A1A3EF]">
        <span className="text-[16px] font-bold text-white">{number}</span>
      </span>

      <label className="flex cursor-pointer items-center gap-3">
        <input
          type="checkbox"
          className="h-[35px] lg:h-[22px] w-[35px] lg:w-[22px] cursor-pointer accent-[#006AF6]"
          checked={checked}
          onChange={onChange}
          aria-label={`Toggle section ${number}: ${title}`}
        />
        <span className="text-[22px] text-[#404040]">{title}</span>
      </label>
    </div>
  );
}

function BdePackageCard({ pkg, selected, onSelect }) {
  const formatPrice = (value) => Number(value).toFixed(2);
  const badgeSrc = `${process.env.PUBLIC_URL || ""}/badge.svg`;

  return (
    <div className="main-card relative h-[600px] w-[400px] rounded-2xl bg-[#410F85]">
      {/* Same Special Discount ribbon used in enrollment packages */}
      <div className="absolute top-[-17px] right-[-19px] z-[1]">
        <img
          className="w-[150px]"
          src={badgeSrc}
          alt={`${pkg.name} special discount badge`}
        />
      </div>

      <div className="card-top rounded-t-2xl bg-[#4C07AB] py-5 text-white">
        <div className="flex flex-col items-center justify-center gap-5">
          <h2 className="max-w-[200px] text-center text-3xs md:text-xs">
            {pkg.name}
          </h2>
          <p>
            <span className="px-5 text-xs line-through decoration-[#ea52c6] decoration-2 md:text-sm">
              ${formatPrice(pkg.originalPrice)}
            </span>{" "}
            <span className="text-xs md:text-sm">
              ${formatPrice(pkg.price)}
            </span>
          </p>
        </div>
      </div>

      <div className="rounded-b-2xl bg-[#410F85] px-3 py-5">
        <div className="card-bottom flex flex-col gap-5 px-8 py-3">
          {pkg.features.map((feature) => (
            <div key={feature} className="flex gap-3 text-white">
              <span className="material-symbols-outlined text-white">
                task_alt
              </span>
              <p>{feature}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="btn-div absolute bottom-0 flex w-full items-center justify-center pb-10">
        <button
          type="button"
          onClick={onSelect}
          className={`SelectButton cursor-pointer rounded-2xl border py-2 px-5 text-4xs sm:py-[14px] sm:text-2xs md:text-3xs lg:text-3xs xl:text-xs 2xl:text-2xs ${
            selected
              ? "bg-[#ea52c6] text-white"
              : "bg-transparent text-white"
          }`}
          style={{ border: "1px solid #ea52c6" }}
        >
          {selected ? "Selected" : "Select Package"}
        </button>
      </div>
    </div>
  );
}

function G2GServiceSelection({ sections, onSectionsChange }) {
  const { bde, inVehicle, driveTest } = sections;

  const toggleBde = (e) => {
    const checked = e.target.checked;
    onSectionsChange({
      ...sections,
      bde: {
        checked,
        packageId: checked ? bdePackages[0]?.id ?? null : null,
      },
    });
  };

  const toggleInVehicle = (e) => {
    const checked = e.target.checked;
    onSectionsChange({
      ...sections,
      inVehicle: {
        checked,
        optionIndex: checked ? 0 : null,
        customHours: checked ? inVehicle.customHours : "",
      },
    });
  };

  const toggleDriveTest = (e) => {
    const checked = e.target.checked;
    onSectionsChange({
      ...sections,
      driveTest: {
        checked,
        optionIndex: checked ? 0 : null,
      },
    });
  };

  const selectBdePackage = (packageId) => {
    // Clicking the already-selected package unselects it and unchecks the section
    if (bde.checked && bde.packageId === packageId) {
      onSectionsChange({
        ...sections,
        bde: { checked: false, packageId: null },
      });
      return;
    }
    onSectionsChange({
      ...sections,
      bde: { ...bde, checked: true, packageId },
    });
  };

  const selectInVehicleOption = (optionIndex) => {
    // Clicking the already-selected option unselects it and unchecks the section
    if (inVehicle.checked && inVehicle.optionIndex === optionIndex) {
      onSectionsChange({
        ...sections,
        inVehicle: {
          checked: false,
          optionIndex: null,
          customHours: "",
        },
      });
      return;
    }
    onSectionsChange({
      ...sections,
      inVehicle: {
        ...inVehicle,
        checked: true,
        optionIndex,
        customHours:
          inVehicleOptions[optionIndex]?.custom === true
            ? inVehicle.customHours
            : "",
      },
    });
  };

  const selectDriveTestOption = (optionIndex) => {
    // Clicking the already-selected option unselects it and unchecks the section
    if (driveTest.checked && driveTest.optionIndex === optionIndex) {
      onSectionsChange({
        ...sections,
        driveTest: { checked: false, optionIndex: null },
      });
      return;
    }
    onSectionsChange({
      ...sections,
      driveTest: { ...driveTest, checked: true, optionIndex },
    });
  };

  const selectedInVehicle = inVehicleOptions[inVehicle.optionIndex];
  const showCustomHours =
    inVehicle.checked && selectedInVehicle?.custom === true;

  return (
    <div className="mt-8 flex w-full flex-col gap-8">
      {/* Section 1 — BDE */}
      <section className="flex flex-col gap-5">
        <SectionToggle
          number={1}
          title="BDE MTO approved Certificate Training Course"
          checked={bde.checked}
          onChange={toggleBde}
        />

        {bde.checked && (
          <div className="mt-[20px] relative left-1/2 w-screen -translate-x-1/2">
            <div className="mx-auto flex w-[80%] flex-wrap items-center justify-center gap-[50px]">
              {bdePackages.map((pkg) => (
                <BdePackageCard
                  key={pkg.id}
                  pkg={pkg}
                  selected={bde.packageId === pkg.id}
                  onSelect={() => selectBdePackage(pkg.id)}
                />
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Section 2 — In-Vehicle */}
      <section className="flex flex-col gap-4">
        <SectionToggle
          number={2}
          title="Select package for In-Vehicle Training / Practice"
          checked={inVehicle.checked}
          onChange={toggleInVehicle}
        />

        {inVehicle.checked && (
          <div className="flex flex-col gap-3 pl-1 sm:pl-12">
            {inVehicleOptions.map((option, index) => {
              const optionId = `in-vehicle-${index}`;
              const isCustom = option.custom === true;
              const label = isCustom
                ? "Custom Hours"
                : `${option.hours} hrs in-vehicle training with instructor, Price: $${Number(
                    option.price
                  ).toFixed(2)} (Including Tax)`;

              return (
                <label
                  key={optionId}
                  htmlFor={optionId}
                  className="flex cursor-pointer items-start gap-3 text-[18px] text-[#404040] lg:text-[#A7A7A7]"
                >
                  <input
                    id={optionId}
                    type="radio"
                    name="in-vehicle-option"
                    className="mt-1 h-[30px] w-[30px] lg:h-[20px] lg:w-[20px] accent-[#006AF6]"
                    checked={inVehicle.optionIndex === index}
                    onChange={() => selectInVehicleOption(index)}
                    onClick={() => {
                      // Radios don't fire onChange when already selected — allow unselect
                      if (inVehicle.optionIndex === index) {
                        selectInVehicleOption(index);
                      }
                    }}
                    required={inVehicle.checked}
                  />
                  <span>{label}</span>
                </label>
              );
            })}

            {showCustomHours && (
              <div className="mt-2 flex w-full flex-col gap-2">
                <label
                  htmlFor="custom-in-vehicle-hours"
                  className="text-[22px] text-[#404040]"
                >
                  In-Vehicle Hours
                </label>
                <input
                  id="custom-in-vehicle-hours"
                  type="number"
                  min={2}
                  placeholder="Enter Hours (Min 2)"
                  value={inVehicle.customHours}
                  onChange={(e) =>
                    onSectionsChange({
                      ...sections,
                      inVehicle: {
                        ...inVehicle,
                        customHours: e.target.value,
                      },
                    })
                  }
                  style={{
                    boxShadow: "0px 3px 6px #00000029",
                    border: "1px solid #E8E8E8",
                }}
                  className="w-full h-[44px] rounded-[6px] border border-[#E8E8E8] px-3 text-[18px] outline-none focus:border-[#006AF6]"
                  required
                />
              </div>
            )}
          </div>
        )}
      </section>

      {/* Section 3 — Drive Test */}
      <section className="flex flex-col gap-4">
        <SectionToggle
          number={3}
          title="Book a Vehicle for Drive Test (within city area)"
          checked={driveTest.checked}
          onChange={toggleDriveTest}
        />

        {driveTest.checked && (
          <div className="flex flex-col gap-3 pl-1 sm:pl-12">
            {driveTestOptions.map((option, index) => {
              const optionId = `drive-test-${index}`;
              const label = `${option.label}, Price: $${Number(option.price).toFixed(
                2
              )} (Including Tax)`;

              return (
                <label
                  key={optionId}
                  htmlFor={optionId}
                  className="flex cursor-pointer items-start gap-3 text-[18px] text-[#404040] lg:text-[#A7A7A7]"
                >
                  <input
                    id={optionId}
                    type="radio"
                    name="drive-test-option"
                    className="mt-1 h-[30px] w-[30px] lg:h-[16px] lg:w-[16px] accent-[#006AF6]"
                    checked={driveTest.optionIndex === index}
                    onChange={() => selectDriveTestOption(index)}
                    onClick={() => {
                      // Radios don't fire onChange when already selected — allow unselect
                      if (driveTest.optionIndex === index) {
                        selectDriveTestOption(index);
                      }
                    }}
                    required={driveTest.checked}
                  />
                  <span>{label}</span>
                </label>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

const LicenseTypeSelection = ({ selectionState, setSelectionState }) => {
  const { licenseType, sections } = selectionState;
  const showServiceSections = licenseType === "G2" || licenseType === "G";

  const handleLicenseChange = (e) => {
    const nextLicense = e.target.value;
    setSelectionState((prev) => ({
      ...prev,
      licenseType: nextLicense,
      sections:
        nextLicense === "G1"
          ? {
              bde: { checked: false, packageId: null },
              inVehicle: { checked: false, optionIndex: null, customHours: "" },
              driveTest: { checked: false, optionIndex: null },
            }
          : {
              bde: { checked: true, packageId: 1 },
              inVehicle: { checked: false, optionIndex: null, customHours: "" },
              driveTest: { checked: false, optionIndex: null },
            },
    }));
  };

  const handleSectionsChange = (nextSections) => {
    setSelectionState((prev) => ({
      ...prev,
      sections: nextSections,
    }));
  };

  return (
    <div className="flex w-full justify-center px-4 py-10">
      <div className="flex w-[90%] lg:w-[70%] flex-col gap-2">
        <label
          htmlFor="license-type-select"
          className="text-[22px] font-semibold text-[#404040]"
        >
          Select License Type
        </label>

        <select
          id="license-type-select"
          value={licenseType}
          onChange={handleLicenseChange}
          className="h-[48px] w-full rounded-[6px] border border-[#E8E8E8] bg-white px-3 text-[16px] text-[#000] outline-none"
          style={{
            boxShadow: "0px 3px 6px #00000029",
            border: "1px solid #E8E8E8",
          }}
        >
          {LICENSE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        {licenseType === "G1" && (
          <p className="mt-[20px] text-center text-[18px] leading-7 text-[#000000]">
            Test your knowledge with a quick quiz, or explore more about the G1
            Category{" "}
            <Link
              to="/get-ready/knowledge-test"
              className="font-semibold text-[#006AF6] underline hover:opacity-80"
            >
              here
            </Link>
          </p>
        )}

        {showServiceSections && (
          <G2GServiceSelection
            sections={sections}
            onSectionsChange={handleSectionsChange}
          />
        )}
      </div>
    </div>
  );
};

export default LicenseTypeSelection;
